import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Chip, LinearProgress, Tabs, Tab, Grid, Button } from '@mui/material';
import { UserIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import journeyService from '../services/journeyService';
import JourneyTimelineChart from './JourneyTimelineChart';
import JourneyMetricsPanel from './JourneyMetricsPanel';
import toast from 'react-hot-toast';
import incomeService from '../../income/services/incomeService';
import { getCashFlowSummary } from '../../cashflow/services/cashFlowService';
import walletService from '../../wallet/services/walletService';
import { calculateAllIncomeTotals, calculateMonthlyAmount } from '../../income/utils/incomeUtils';
import { useAuthContext } from '../../auth/context/AuthContext';
import MobileViewSelector from '../../../components/shared/MobileViewSelector';

/**
 * Journey Detail Page
 * 🗺️ Displays a single journey with timelines and roadmap
 */
// Goal display names
const GOAL_DISPLAY_NAMES = {
  'emergency_fund': 'Emergency Fund',
  'pay_off_credit_cards': 'Pay Off Credit Cards',
  'pay_off_loans': 'Pay Off Loans',
  'save_for_house': 'House Down Payment',
  'plan_retirement': 'Retirement Planning',
  'invest_growth': 'Investment Growth',
  'save_education': 'Education Fund',
  'start_business': 'Start Business',
  'major_purchase': 'Major Purchase',
  'financial_independence': 'Financial Independence',
  'major_life_event': 'Major Life Event',
  'other': 'Other Goal'
};

const JourneyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedGoalIndex, setSelectedGoalIndex] = useState(0);
  
  // Partner support: view selector state
  const [activeViewTab, setActiveViewTab] = useState('my'); // 'my', 'our', 'partner'
  const [partnerPermissions, setPartnerPermissions] = useState(null);
  
  // Derive partner mode flags
  const isPartnerMode = activeViewTab === 'partner';
  const isOurMode = activeViewTab === 'our';
  const hasPartner = user?.partner || user?.partnerId || user?.createdPartner;
  const partnerDisplayName = user?.partner?.firstName || user?.createdPartner?.firstName || 'Partner';
  
  // Interactive metrics state for each goal
  const [goalMetrics, setGoalMetrics] = useState({});
  const [timePeriod, setTimePeriod] = useState('annual');
  // Local expenses state to allow independent control
  const [localExpenses, setLocalExpenses] = useState(0);
  // Financial data from other sources
  const [financialData, setFinancialData] = useState({
    grossMonthlyIncome: 0,
    netMonthlyIncome: 0,
    monthlyExpenses: 0,
    currentSavings: 0
  });

  useEffect(() => {
    fetchJourney();
    fetchFinancialData();
  }, [id, activeViewTab]); // Re-fetch when view changes

  // Fetch partner permissions
  useEffect(() => {
    const fetchPartnerPermissions = async () => {
      if (!hasPartner) return;
      
      try {
        let ownerUserId;
        if (user?.partnerId) {
          ownerUserId = user.partnerId;
        } else if (user?.createdPartner) {
          ownerUserId = user._id;
        } else {
          return;
        }

        const axiosInstance = (await import('../../../utils/axiosConfig')).default;
        const response = await axiosInstance.get(`/api/user-sharing/partner-permissions/${ownerUserId}`);
        if (response.data?.success) {
          setPartnerPermissions(response.data.permissions);
        }
      } catch (error) {
        // Silently handle 404 - partner permissions endpoint may not exist for all users
        if (error.response?.status !== 404) {
          console.error('Error fetching partner permissions:', error);
        }
      }
    };

    fetchPartnerPermissions();
  }, [hasPartner, user]);

  // Fetch financial data from Cash Flow, Wallet, and Transactions
  const fetchFinancialData = async () => {
    try {
      // Fetch income data with partner support
      const incomeOptions = {
        partnerAccess: isPartnerMode,
        includePartner: isOurMode && !isPartnerMode
      };
      
      const incomeResponse = await incomeService.getAll(incomeOptions);
      const incomes = incomeResponse.data || [];
      
      // 🔍 DEBUG: Log raw income data
      console.log('📊 [JourneyDetail] Raw income data:', JSON.stringify(incomes, null, 2));
      
      // Calculate gross and net monthly income using proper utility
      const incomeTotals = calculateAllIncomeTotals(incomes);
      let grossMonthly = incomeTotals.gross || 0;
      let netMonthly = incomeTotals.net || 0;
      
      // 🔍 DEBUG: Log calculated totals
      console.log('📊 [JourneyDetail] Calculated income totals:', {
        gross: grossMonthly,
        net: netMonthly,
        tax: incomeTotals.tax
      });
      
      // CRITICAL FIX: Validate and correct Gross/Net relationship
      // Gross MUST always be >= Net (taxes reduce gross to net)
      if (netMonthly > grossMonthly) {
        console.error('❌ [JourneyDetail] INVALID: Net > Gross detected!', {
          gross: grossMonthly,
          net: netMonthly,
          difference: netMonthly - grossMonthly
        });
        
        // If net is significantly higher, the data is corrupted - recalculate
        if (netMonthly > grossMonthly * 1.1) {
          console.warn('⚠️ [JourneyDetail] Net is >10% higher than Gross - data appears corrupted. Recalculating...');
          // Use net as gross (it's likely the actual gross amount)
          grossMonthly = netMonthly;
          // Recalculate net as 77% of gross (estimate 23% tax)
          netMonthly = grossMonthly * 0.77;
        } else {
          // If close, just swap them (calculation error)
          console.warn('⚠️ [JourneyDetail] Swapping Gross and Net values');
          const temp = grossMonthly;
          grossMonthly = netMonthly;
          netMonthly = temp;
        }
      }
      
      // Final validation: Ensure gross is at least 10% higher than net (reasonable tax rate)
      if (grossMonthly > 0 && netMonthly >= grossMonthly * 0.95) {
        console.warn('⚠️ [JourneyDetail] Net is too close to Gross (tax rate <5%). Adjusting...');
        // If net is too close to gross, recalculate net with minimum 15% tax
        netMonthly = grossMonthly * 0.85;
      }
      
      // Ensure gross is always >= net
      if (netMonthly > grossMonthly) {
        grossMonthly = netMonthly * 1.2; // Set gross to 20% higher than net
        netMonthly = grossMonthly * 0.77; // Recalculate net
      }
      
      // 🔍 DEBUG: Log final values
      console.log('✅ [JourneyDetail] Final income values:', {
        grossMonthlyIncome: grossMonthly,
        netMonthlyIncome: netMonthly,
        taxRate: grossMonthly > 0 ? ((grossMonthly - netMonthly) / grossMonthly * 100).toFixed(1) + '%' : '0%'
      });
      
      // Fetch cash flow data for expenses
      let monthlyExpenses = 0;
      try {
        const cashFlowData = await getCashFlowSummary();
        monthlyExpenses = cashFlowData?.totalMonthlyExpenses || 0;
      } catch (error) {
        console.error('Error fetching cash flow:', error);
      }
      
      // Fetch wallet data for current savings
      const accountsResponse = await walletService.getAccounts();
      const accounts = accountsResponse.data || [];
      const currentSavings = accounts
        .filter(acc => acc.accountType === 'savings')
        .reduce((sum, acc) => sum + (acc.balance || 0), 0);
      
      setFinancialData({
        grossMonthlyIncome: grossMonthly, // Gross should be higher (before taxes)
        netMonthlyIncome: netMonthly, // Net should be lower (after taxes)
        monthlyExpenses: monthlyExpenses,
        currentSavings: currentSavings
      });
    } catch (error) {
      console.error('❌ [JourneyDetail] Error fetching financial data:', error);
      // Don't show error toast, just use journey data as fallback
    }
  };

  // Initialize goal metrics when journey loads
  useEffect(() => {
    if (journey && journey.goals) {
      const initialMetrics = {};
      const netIncome = journey.income?.netMonthly || 0;
      const expenses = journey.currentFinancials?.monthlyExpenses || 0;
      const recommendedSavings = Math.max(0, netIncome - expenses);
      
      // Initialize local expenses state
      setLocalExpenses(expenses);
      
      journey.goals.forEach((goalId, index) => {
        const goalDetail = journey.goalDetails?.[goalId] || {};
        const timeline = journey.plan?.timelines?.find(t => t.goalId === goalId);
        
        // Smart target amount calculation based on goal type and user data
        let recommendedTarget = 0;
        const currentAge = journey.startingAge || 30;
        const retirementAge = goalDetail.targetAge || 65;
        const yearsToRetirement = retirementAge - currentAge;
        
        if (goalId === 'financial_independence' && expenses > 0) {
          // FIRE number: 25x annual expenses (4% rule)
          recommendedTarget = expenses * 12 * 25;
        } else if (goalId === 'plan_retirement' && expenses > 0) {
          // Retirement: 25x annual expenses (4% rule) - adjust for inflation
          const annualExpenses = expenses * 12;
          // Project expenses to retirement age with 3% inflation
          const inflatedExpenses = annualExpenses * Math.pow(1.03, yearsToRetirement);
          recommendedTarget = inflatedExpenses * 25;
        } else if (goalId === 'invest_growth') {
          // Investment growth: Aim for 10-20x current annual income
          recommendedTarget = netIncome * 12 * 15; // 15x annual income
        } else if (goalId === 'save_for_house') {
          // House down payment: 20% of median home price or 2x annual income
          recommendedTarget = Math.max(netIncome * 12 * 2, 50000); // At least $50k
        } else if (goalId === 'emergency_fund') {
          // Emergency fund: 6 months of expenses
          recommendedTarget = expenses * 6;
        } else if (goalId === 'save_education') {
          // Education: Average 4-year college cost (~$100k-200k)
          recommendedTarget = 150000;
        } else {
          // Default: Use existing target or calculate based on income
          recommendedTarget = goalDetail.targetAmount || timeline?.data?.[0]?.goalAmount || netIncome * 12 * 5;
        }
        
        // Calculate recommended savings based on budgeting style (default 20% of net income)
        const recommendedMonthlySavings = recommendedSavings > 0 ? recommendedSavings : Math.max(0, netIncome * 0.2);
        
        initialMetrics[goalId] = {
          currentAmount: journey.currentFinancials?.savings || financialData.currentSavings || 0,
          targetAmount: goalDetail.targetAmount || recommendedTarget,
          monthlySavings: recommendedMonthlySavings, // Default to recommended savings
          interestRate: 4.0, // Default 4% expected annual return
          retirementAge: retirementAge
        };
      });
      setGoalMetrics(initialMetrics);
    }
  }, [journey]);

  // Calculate available monthly savings
  const calculateAvailableSavings = (journey) => {
    const netIncome = journey.income?.netMonthly || 0;
    const expenses = journey.currentFinancials?.monthlyExpenses || 0;
    const debtPayments = (journey.currentFinancials?.debts || [])
      .reduce((sum, d) => sum + (d.minimumPayment || 0), 0);
    
    const available = netIncome - expenses - debtPayments;
    return Math.max(0, available * 0.8); // Use 80% of available
  };

  const fetchJourney = async () => {
    try {
      setLoading(true);
      const response = await journeyService.getJourney(id);
      if (response.success) {
        setJourney(response.data);
      }
    } catch (error) {
      console.error('Error fetching journey:', error);
      toast.error('Failed to load journey');
      navigate('/journeys');
    } finally {
      setLoading(false);
    }
  };

  const handleMetricChange = (goalId, metric, value) => {
    setGoalMetrics(prev => ({
      ...prev,
      [goalId]: {
        ...prev[goalId],
        [metric]: value
      }
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ width: 48, height: 48, border: '3px solid', borderColor: 'primary.main', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', mx: 'auto', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">Loading journey...</Typography>
        </Box>
      </Box>
    );
  }

  if (!journey) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 12, px: 4 }}>
        <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>Journey not found</Typography>
          <button
            onClick={() => navigate('/journeys')}
            style={{ padding: '12px 24px', backgroundColor: '#6366f1', color: 'white', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
          >
            Back to Journeys
          </button>
        </Box>
      </Box>
    );
  }

  const selectedGoalId = journey.goals?.[selectedGoalIndex];
  const netIncome = financialData.netMonthlyIncome || journey.income?.netMonthly || 0;
  const expenses = localExpenses || financialData.monthlyExpenses || journey.currentFinancials?.monthlyExpenses || 0;
  const recommendedSavings = Math.max(0, netIncome - expenses);
  const selectedGoalMetrics = goalMetrics[selectedGoalId] || {
    currentAmount: journey.currentFinancials?.savings || 0,
    targetAmount: 10000,
    monthlySavings: recommendedSavings > 0 ? recommendedSavings : Math.max(0, netIncome * 0.2), // Default to recommended
    interestRate: 4.0
  };

  // Calculate time to goal in months for chart auto-adjustment
  const calculateTimeToGoalMonths = () => {
    const { currentAmount, targetAmount, monthlySavings, interestRate } = selectedGoalMetrics;
    if (!monthlySavings || monthlySavings <= 0) return null;
    
    const monthlyRate = (interestRate / 100) / 12;
    const remaining = targetAmount - currentAmount;
    
    if (remaining <= 0) return 0;
    
    let months = 0;
    let amount = currentAmount;
    const maxMonths = 1200; // 100 years max
    
    while (amount < targetAmount && months < maxMonths) {
      amount = amount * (1 + monthlyRate) + monthlySavings;
      months++;
      if (amount >= targetAmount) break;
    }
    
    return months >= maxMonths ? null : months; // Return null if exceeds max
  };
  
  const timeToGoalMonths = calculateTimeToGoalMonths();

  // Partner view selector views
  const viewOptions = hasPartner ? [
    {
      value: 'my',
      label: 'My Journey',
      icon: <UserIcon className="h-5 w-5" />
    },
    {
      value: 'our',
      label: 'Our Journey',
      icon: <UserGroupIcon className="h-5 w-5" />
    },
    {
      value: 'partner',
      label: `${partnerDisplayName}'s Journey`,
      icon: <UserIcon className="h-5 w-5" />,
      disabled: !partnerPermissions?.hasFullCashflowAccess,
      title: partnerPermissions?.hasFullCashflowAccess 
        ? `View ${partnerDisplayName}'s journey data`
        : 'Partner has not granted access'
    }
  ] : [
    {
      value: 'my',
      label: 'My Journey',
      icon: <UserIcon className="h-5 w-5" />
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4, px: { xs: 2, sm: 4, md: 6 } }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <button
            onClick={() => navigate('/journeys')}
            style={{ color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '16px', display: 'flex', alignItems: 'center' }}
          >
            ← Back to Journeys
          </button>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            {journey.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Created {new Date(journey.createdAt).toLocaleDateString()}
          </Typography>
        </Box>

        {/* Partner View Selector */}
        {hasPartner && (
          <Box sx={{ mb: 3 }}>
            <MobileViewSelector
              title="Income Source"
              currentView={activeViewTab}
              onViewChange={(view) => {
                setActiveViewTab(view);
              }}
              views={viewOptions}
            />
          </Box>
        )}

        {/* Goals Tabs */}
        {journey.goals && journey.goals.length > 1 && (
          <Box sx={{ mb: 3 }}>
            <Tabs
              value={selectedGoalIndex}
              onChange={(e, newValue) => setSelectedGoalIndex(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {journey.goals.map((goalId, index) => (
                <Tab
                  key={goalId}
                  label={GOAL_DISPLAY_NAMES[goalId] || goalId.replace(/_/g, ' ')}
                />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Journey Overview - Full Width at Top */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom>
                  Goals
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {journey.goals?.map((goal, idx) => (
                    <Chip
                      key={idx}
                      label={GOAL_DISPLAY_NAMES[goal] || goal.replace(/_/g, ' ')}
                      color={idx === selectedGoalIndex ? 'primary' : 'default'}
                      size="small"
                    />
                  ))}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom>
                  Overall Progress
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={journey.progress?.overall || 0}
                  sx={{ height: 8, borderRadius: 4, mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {journey.progress?.overall || 0}% Complete
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom>
                  Financial Summary
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Current Savings: ${(journey.currentFinancials?.savings || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly Expenses: ${(journey.currentFinancials?.monthlyExpenses || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Net Monthly Income: ${(financialData.netMonthlyIncome || journey.income?.netMonthly || 0).toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Metrics Panel - Full Width */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <JourneyMetricsPanel
              currentAmount={selectedGoalMetrics.currentAmount || financialData.currentSavings}
              targetAmount={selectedGoalMetrics.targetAmount}
              monthlySavings={selectedGoalMetrics.monthlySavings}
              monthlyIncome={financialData.netMonthlyIncome || journey.income?.netMonthly || 0}
              grossMonthlyIncome={financialData.grossMonthlyIncome || (journey.income?.grossAnnual || 0) / 12}
              monthlyExpenses={localExpenses || financialData.monthlyExpenses || journey.currentFinancials?.monthlyExpenses || 0}
              interestRate={selectedGoalMetrics.interestRate}
              onCurrentAmountChange={(value) => handleMetricChange(selectedGoalId, 'currentAmount', value)}
              onTargetAmountChange={(value) => handleMetricChange(selectedGoalId, 'targetAmount', value)}
              onMonthlySavingsChange={(value) => handleMetricChange(selectedGoalId, 'monthlySavings', value)}
              onExpensesChange={(value) => {
                // Update local expenses state independently
                setLocalExpenses(value);
              }}
              onInterestRateChange={(value) => handleMetricChange(selectedGoalId, 'interestRate', value)}
            />
            
            {/* Save Button */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={async () => {
                  try {
                    // Prepare update data
                    const updateData = {
                      goalDetails: {
                        ...journey.goalDetails,
                        [selectedGoalId]: {
                          ...journey.goalDetails?.[selectedGoalId],
                          targetAmount: selectedGoalMetrics.targetAmount,
                          currentAmount: selectedGoalMetrics.currentAmount,
                          monthlySavings: selectedGoalMetrics.monthlySavings,
                          interestRate: selectedGoalMetrics.interestRate
                        }
                      },
                      currentFinancials: {
                        ...journey.currentFinancials,
                        savings: selectedGoalMetrics.currentAmount,
                        monthlyExpenses: localExpenses || journey.currentFinancials?.monthlyExpenses
                      }
                    };
                    
                    const response = await journeyService.updateJourney(id, updateData);
                    if (response.success) {
                      toast.success('Journey saved successfully!');
                      // Optionally regenerate plan
                      try {
                        await journeyService.generatePlan(id);
                      } catch (error) {
                        console.error('Error regenerating plan:', error);
                        // Don't show error, plan regeneration is optional
                      }
                    } else {
                      toast.error('Failed to save journey');
                    }
                  } catch (error) {
                    console.error('Error saving journey:', error);
                    toast.error('Error saving journey');
                  }
                }}
                sx={{ minWidth: 120 }}
              >
                Save Changes
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Chart - Full Width at Bottom */}
        <Card>
          <CardContent>
            <JourneyTimelineChart
              goalName={GOAL_DISPLAY_NAMES[selectedGoalId] || selectedGoalId}
              currentAmount={selectedGoalMetrics.currentAmount}
              targetAmount={selectedGoalMetrics.targetAmount}
              monthlySavings={selectedGoalMetrics.monthlySavings}
              interestRate={selectedGoalMetrics.interestRate}
              timePeriod={timePeriod}
              onTimePeriodChange={setTimePeriod}
              timeToGoalMonths={timeToGoalMonths}
            />
          </CardContent>
        </Card>

        {/* Withdrawal Scenarios - Below Chart */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Withdrawal Scenarios
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              See what your target amount can provide with different withdrawal rates
            </Typography>
            <Grid container spacing={2}>
              {[3, 4, 5, 6, 7, 8, 10].map((withdrawalRate) => {
                const annualWithdrawal = selectedGoalMetrics.targetAmount * (withdrawalRate / 100);
                const monthlyWithdrawal = annualWithdrawal / 12;
                const netReturn = selectedGoalMetrics.interestRate - withdrawalRate;
                const isSustainable = netReturn > 0;
                const yearsToDeplete = netReturn <= 0 ? 0 : Math.log(0.01) / Math.log(1 - (netReturn / 100));
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={withdrawalRate}>
                    <Box sx={{ p: 2, bgcolor: isSustainable ? 'success.50' : 'error.50', borderRadius: 1, border: '1px solid', borderColor: isSustainable ? 'success.200' : 'error.200' }}>
                      <Typography variant="body1" fontWeight={600} color={isSustainable ? 'success.main' : 'error.main'} gutterBottom>
                        {withdrawalRate}% Withdrawal
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Annual: <strong>${annualWithdrawal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Monthly: <strong>${monthlyWithdrawal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>
                      </Typography>
                      <Typography variant="body2" color={isSustainable ? 'success.main' : 'error.main'} sx={{ mt: 1, fontWeight: 600 }}>
                        {isSustainable 
                          ? `✓ Sustainable (${netReturn.toFixed(1)}% net growth)`
                          : `⚠️ Depleting (${Math.abs(netReturn).toFixed(1)}% net loss)`
                        }
                      </Typography>
                      {!isSustainable && yearsToDeplete > 0 && (
                        <Typography variant="caption" display="block" color="error.main" sx={{ mt: 0.5 }}>
                          Depletes in ~{Math.round(yearsToDeplete)} years
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default JourneyDetail;

